import { onMounted, onUnmounted, nextTick, watch } from 'vue'
import type { Ref } from 'vue'
import { useKeyboardNavigationDetection } from '~/composables/useKeyboardNavigationDetection'

function useFocusTrap (
  containerRef: Ref<HTMLElement | null>,
  isActive: Ref<boolean>,
  additionalItems: Ref<HTMLElement | null>[] = [],
  callback?: () => void
) {
  const { isUsingKeyboard } = useKeyboardNavigationDetection()

  const focusableSelector =
    'iframe, a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), *[tabindex="0"]'

  const getUnifiedFocusableElements = (): HTMLElement[] => {
    const containerEls = containerRef.value
      ? Array.from(containerRef.value.querySelectorAll<HTMLElement>(focusableSelector))
      : []

    const additionalEls = additionalItems.map(ref => ref.value).filter((el): el is HTMLElement => !!el)

    return [...containerEls, ...additionalEls]
  }

  const focusFirstElement = async () => {
    await nextTick()
    const elements = getUnifiedFocusableElements()

    if (elements[0]) {
      const interval = window.setInterval(() => {
        if (document.activeElement !== elements[0]) {
          elements[0]?.focus()
        } else {
          clearInterval(interval)
        }
      }, 125)
    }
  }

  const handleKeydown = (event: KeyboardEvent) => {
    const elements = getUnifiedFocusableElements()

    if (!isActive.value) { return }
    if (event.key !== 'Tab') { return }

    event.preventDefault()

    if (elements.length === 0) { return }

    const current = document.activeElement as HTMLElement
    const index = elements.findIndex(el => el === current)

    if (event.shiftKey) {
      if (index > 0 && index !== -1) {
        elements[index - 1].focus()
      } else {
        elements[elements.length - 1].focus()
      }
    } else if (index < elements.length - 1 && index !== -1) {
      elements[index + 1].focus()
    } else {
      elements[0].focus()
    }
  }

  watch(
    isActive,
    (value) => {
      if (value) {
        if (isUsingKeyboard.value) { focusFirstElement() }
      } else {
        callback?.()
      }
    },
    { immediate: true }
  )

  onMounted(() => {
    document.addEventListener('keydown', handleKeydown)
  })

  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown)
  })
}

export default useFocusTrap
